import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeader from "@/components/SectionHeader";

const CORE_PRACTICES = [
  {
    name: "Business Strategy & Executive Advisory",
    summary: "Helping leadership teams define direction, improve performance, and make confident strategic decisions.",
  },
  {
    name: "Digital Transformation",
    summary: "Modernizing operations, systems, and technology to improve efficiency, scalability, and competitive advantage.",
  },
  {
    name: "Operational Excellence",
    summary: "Optimizing business processes, supply chains, quality systems, and organizational performance.",
  },
  {
    name: "Artificial Intelligence & Automation",
    summary: "Designing practical AI and intelligent automation solutions that create measurable business value.",
  },
  {
    name: "CRM & PLM Solutions",
    summary: "Connecting customer relationships, engineering, manufacturing, and lifecycle operations into one intelligent ecosystem.",
  },
  {
    name: "Business Intelligence & Analytics",
    summary: "Transforming data into executive insight through dashboards, reporting, forecasting, and decision intelligence.",
  },
  {
    name: "Compliance, Risk & Governance",
    summary: "Building secure, compliant, and audit-ready organizations with governance designed for long-term resilience.",
  },
  {
    name: "Enterprise Technology & Custom Software",
    summary: "Designing and delivering enterprise platforms, business applications, integrations, and scalable digital solutions tailored to each organization.",
  },
];

const SERVED_INDUSTRIES = [
  "Industrial Manufacturing",
  "Automotive & Suppliers",
  "Healthcare & Life Sciences",
  "Financial Services",
  "Aerospace & Defense",
  "Energy & Utilities",
  "Public Sector",
  "Consumer & Retail",
  "Hospitality",
  "Technology",
];

export default function Services() {
  return (
    <div data-testid="services-page" className="pt-32 pb-24">
      <Seo
        title="Our Expertise"
        description="Strategic expertise, practical execution, and measurable results — integrated consulting across strategy, technology, operations, AI, and growth."
        path="/services"
      />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 lg:col-span-8">
            <span className="overline">Our Expertise</span>
            <h1 className="font-display text-5xl sm:text-7xl text-[#001B54] mt-6 leading-[1.02]">
              Strategic expertise. Practical execution.
              <span className="block italic gold-text">Measurable results.</span>
            </h1>
            <p className="mt-8 text-[#3F4A6B] text-base sm:text-lg max-w-2xl leading-relaxed">
              At AlchamisGroup, we help organizations solve complex business challenges through a single
              integrated approach that combines strategy, technology, operations, and execution.
            </p>
            <p className="mt-5 text-[#3F4A6B] text-base sm:text-lg max-w-2xl leading-relaxed">
              Rather than engaging multiple consulting firms, our clients work with one trusted partner
              who coordinates every phase — from strategic planning to implementation, optimization, and
              long-term growth.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white border border-[#001B54]/10 shadow-[0_24px_60px_-30px_rgba(0,27,84,0.18)] p-8">
              <div className="overline mb-4">Engagement Model</div>
              <ul className="space-y-3 text-sm text-[#3F4A6B]">
                <li><span className="text-gold">01.</span> Executive Intake &amp; Diagnostic</li>
                <li><span className="text-gold">02.</span> Strategic Blueprint</li>
                <li><span className="text-gold">03.</span> Joint Implementation</li>
                <li><span className="text-gold">04.</span> Adoption &amp; Governance</li>
                <li><span className="text-gold">05.</span> Quarterly Review &amp; Compounding</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-8">
            <SectionHeader eyebrow="Practices" title="Our core practices." titleClassName="text-[#001B54]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#001B54]/10">
            {CORE_PRACTICES.map((p, idx) => (
              <div
                key={p.name}
                data-testid={`practice-card-${idx + 1}`}
                className="group border-r border-b border-[#001B54]/10 p-8 lg:p-10 bg-white flex flex-col"
              >
                <div className="number-badge">{String(idx + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl text-[#001B54] mt-5 mb-3 leading-tight">{p.name}</h3>
                <p className="text-[#3F4A6B] text-sm leading-relaxed flex-1">{p.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-24 max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-5">
          <SectionHeader eyebrow="Industries" title="Industries we serve." titleClassName="text-[#001B54]" />
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#001B54]/8">
          {SERVED_INDUSTRIES.map((name) => (
            <div key={name} className="bg-white p-6 lg:p-7">
              <div className="text-[#001B54] text-sm">{name}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-24 max-w-[1100px] mx-auto px-6 md:px-12 text-center">
        <span className="overline">One partner</span>
        <h2 className="font-display text-4xl sm:text-5xl text-[#001B54] leading-tight mt-6">
          One partner. Every stage of transformation.
        </h2>
        <p className="text-[#3F4A6B] mt-6 max-w-2xl mx-auto leading-relaxed">
          From strategic planning to enterprise software, operational improvement, AI, and business
          growth, AlchamisGroup delivers integrated consulting designed to create lasting competitive
          advantage.
        </p>
        <p className="font-display text-xl sm:text-2xl text-[#001B54] mt-8 italic">
          Built for complexity. <span className="gold-text">Designed for simplicity.</span>
        </p>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link to="/ai-advisor" className="luxury-button" data-testid="services-cta-advisor">Begin Diagnostic <ArrowRight size={14} /></Link>
          <Link to="/contact" className="luxury-button-ghost">Speak to Partner</Link>
        </div>
      </section>
    </div>
  );
}
